import { useState } from 'react';
import { timeAgo } from '../lib/timeAgo';
import type { Kid } from '../types';

interface Props {
  kid: Kid;
  onChange: (slug: string, available: boolean) => Promise<void>;
}

export function KidToggle({ kid, onChange }: Props) {
  const [busy, setBusy] = useState(false);
  const label = kid.available ? 'Can play' : 'Not right now';

  async function handleClick() {
    setBusy(true);
    try {
      await onChange(kid.slug, !kid.available);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <button
        type="button"
        aria-pressed={kid.available}
        aria-label={`${kid.name} ${kid.available ? 'can play' : 'is not available'}. Activate to change.`}
        disabled={busy}
        onClick={handleClick}
        className={[
          'flex min-h-15 w-full cursor-pointer items-center justify-between gap-4 rounded-2xl border px-4.5 py-4 text-left transition-colors duration-200',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300',
          'disabled:cursor-progress disabled:opacity-70',
          kid.available ? 'border-play-edge bg-play' : 'border-busy-edge bg-busy',
        ].join(' ')}
      >
        <span className="text-xl font-semibold">{kid.name}</span>
        <span className="flex items-center gap-2 text-sm opacity-95">
          {label}
          <span aria-hidden="true" className="size-2.5 rounded-full bg-current opacity-85" />
        </span>
      </button>
      <time dateTime={kid.updated_at} className="mt-1.5 ml-1.5 block text-xs text-ink-muted tabular-nums">
        Updated {timeAgo(kid.updated_at)}
      </time>
    </li>
  );
}
